/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Suspense, use, type ReactElement, type ReactNode } from 'react';
import type { ShopperProducts } from '@/scapi';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
// @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
import { ProductRatingSummary } from './product-rating-summary';
// @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
import { useTranslation } from 'react-i18next';
import { WishlistButton } from '@/components/buttons/wishlist-button';
import { ShareButton } from '@/components/buttons/share-button';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/typography';
import { UITarget } from '@/targets/ui-target';
import BrandLogo from '@/components/brand-logo';
import { resolveBrandKey, type BrandKey } from '@/components/brand-logo/brands';

/**
 * Streams the resolved master model code into the identifier line.
 *
 * `use()` rather than React Router's `<Await>`, so `<ProductInfo>` stays usable outside a
 * data-router context — the modals and Storybook render it directly. The loader attaches a
 * `.catch()`, so a failed master fetch arrives as `null` and never as a rejection to handle here.
 */
function MasterModelCode({ promise }: { promise: Promise<string | null> }): ReactNode {
    const modelCode = use(promise);

    if (!modelCode) return null;

    return modelCode;
}

type HeaderTextProps = {
    product: ShopperProducts.schemas['Product'];
    brandKey: BrandKey | null;
    brandText: string | undefined;
};

/**
 * Header text in the CRCB2B-313 PDP treatment: brand, `heading/md` title and the bare model code.
 *
 * Gap-spaced rather than margin-spaced, mirroring Figma's own nesting (header node 13546:36947):
 * brand and title frame 12px apart, title and model code 2px apart inside it.
 */
function PdpHeaderText({
    product,
    brandKey,
    brandText,
    modelName,
    modelCodePromise,
}: HeaderTextProps & {
    modelName?: string | null;
    modelCodePromise?: Promise<string | null>;
}): ReactElement {
    return (
        <div className="flex flex-col gap-3">
            {/* Text treatment is the fallback, not an error state — the catalogue carries third-party
                brands. h-6 is measured: Figma's Brand Logo instance (13588:36899) is 96x24. */}
            {brandKey ? (
                // `self-start` is required: the parent is a flex COLUMN, so `align-items: stretch`
                // widens this `w-auto` <img> to the full column and `object-contain` then centres the
                // artwork. Not fixable on the container (`items-start` would un-stretch the title) nor
                // in BrandLogo's defaults (in a flex ROW `self-start` means top-aligned).
                <BrandLogo brand={brandKey} className="h-6 self-start" />
            ) : (
                brandText && (
                    <p className="text-sm font-bold leading-none uppercase tracking-wide text-card-foreground">
                        {brandText}
                    </p>
                )
            )}
            <div className="flex flex-col gap-0.5">
                {/* Figma `heading/md` is exactly `variant="h3"`; `as="h1"` keeps the SEO semantics.
                    `??` not `||` — the resolver already collapses blank values to `null`. */}
                <Typography variant="h3" as="h1" data-testid="product-title" className="text-card-foreground">
                    {modelName ?? product.name}
                </Typography>
                {/* Bare, no label prefix, per Figma. Master model code when the loader resolved one,
                    else this product's own id. */}
                {/* `as="div"` is REQUIRED: `muted` defaults to `<p>`, `<Skeleton>` renders a `<div>`,
                    and `<p>` has an implied end tag before block content — so an SSR'd
                    `<p><div/></p>` parses as `<p></p><div/>`, orphaning the fallback below the
                    resolved code and animating forever. jsdom and Storybook build via
                    `createElement` and permit div-in-p, so no suite here can catch it.
                    Do not drop `as="div"`. */}
                {modelCodePromise ? (
                    <Typography variant="muted" as="div">
                        {/* Width-reserving fallback, not `null` — this line is above the fold. */}
                        <Suspense fallback={<Skeleton className="inline-block h-3 w-32 align-middle" />}>
                            <MasterModelCode promise={modelCodePromise} />
                        </Suspense>
                    </Typography>
                ) : (
                    product.id && (
                        <Typography variant="muted" as="div">
                            {product.id}
                        </Typography>
                    )
                )}
            </div>
        </div>
    );
}

/**
 * Header text in the pre-CRCB2B-313 treatment, used by the cart and bonus modals.
 *
 * Kept apart from {@link PdpHeaderText} so PDP type changes cannot reach those surfaces. The
 * identifier keeps its `SKU:` label — in a modal the number identifies one specific variant, and
 * unlabelled it tells a shopper nothing.
 */
function FullHeaderText({
    product,
    brandKey,
    brandText,
    skuLabel,
}: HeaderTextProps & { skuLabel: string }): ReactElement {
    return (
        <>
            {/* No `self-start` needed — this branch is block flow, so nothing stretches. */}
            {brandKey ? (
                <BrandLogo brand={brandKey} className="mb-1 h-6" />
            ) : (
                brandText && (
                    <p className="mb-1 text-xs font-normal leading-none uppercase tracking-wide text-secondary-foreground">
                        {brandText}
                    </p>
                )
            )}
            <h1 data-testid="product-title" className="text-3xl font-bold text-card-foreground tracking-tight">
                {product.name}
            </h1>
            {product.id && (
                <p className="mt-2 text-xs leading-none text-secondary-foreground">
                    {skuLabel} {product.id}
                </p>
            )}
        </>
    );
}

type ProductInfoProps = {
    product: ShopperProducts.schemas['Product'];
    /**
     * Layout and typography style for the header.
     *
     * - `'full'` (default) — brand, `<h1>`, `SKU: <id>` and description, margin-spaced. The cart and
     *   bonus modals' treatment, unchanged since before CRCB2B-313.
     * - `'compact'` — uppercase brand over a smaller `<h2>`. Stories only, no production caller.
     * - `'pdp'` — CRCB2B-313: `heading/md` title, unlabelled identifier, bolder brand, gap-spaced.
     *
     * `'pdp'` is a separate value rather than the new default because `'full'` is shared — anything
     * visual and PDP-specific belongs here, or it silently restyles both modals.
     */
    variantStyle?: 'full' | 'compact' | 'pdp';
    /** Hide top-right action icons (wishlist/share) */
    hideActionIcons?: boolean;
    /** Optional action content rendered inline with title in full variant style */
    headerAction?: ReactNode;
    /**
     * Hide the short description. Per Figma the PDP header ends at the model code — the copy lives
     * in the Details panel instead — so only the PDP opts out; the modals keep it.
     */
    hideDescription?: boolean;
    /**
     * Deferred master-level `c_model-code` for the identifier line, composed in the route loader.
     * The MASTER's code, not the resolved variant's id, and rendered bare per Figma. Read by the
     * `pdp` style only — `full` keeps `SKU: <product.id>`, since a modal identifies one variant and
     * the label is what tells a shopper what the number is.
     */
    modelCodePromise?: Promise<string | null>;
    /**
     * Master-level `c_model-name-for-web` for the heading. When supplied it REPLACES `product.name`:
     * per the PIM definition this is the model-level name ("Super Deluxe Ultimate"), where
     * `product.name` is the fuller catalogue string ("Super Deluxe Ultimate Rear Shock").
     *
     * A plain string, not a promise like {@link ProductInfoProps.modelCodePromise} beside it — a
     * heading is critical data, so the loader awaits it rather than streaming it. Read by the `pdp`
     * style only.
     */
    modelName?: string | null;
    /**
     * Render the brand as its mark when it resolves to one of the eight SRAM-family logos (Figma
     * header node 13546:36947). Orthogonal to {@link ProductInfoProps.variantStyle} — an asset
     * choice, not a type treatment — so either header style takes it. An unrecognised brand falls
     * back to text either way.
     */
    showBrandLogo?: boolean;
    // @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS
    /** Hide the rating summary. Absent from the B2B PDP header (CRCB2B-505); the modals keep it. */
    hideRating?: boolean;
    /** Disable rating summary interactions (hover popover and review links) */
    disableRatingInteraction?: boolean;
    // @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS
};

/**
 * ProductInfo displays product identity: brand, title, identifier, short description, rating
 * summary, wishlist/share actions and the set/bundle notice.
 *
 * Price, inventory, swatches, delivery options and the quantity picker live in the sibling
 * {@link ProductPurchasePanel} (split out in CRCB2B-313). Surfaces that need both compose
 * them together; the B2B PDP renders identity only and supplies its own order grid.
 *
 * @param props - Component props
 * @param props.product - The product data to display
 * @returns JSX element with product identity display
 */
export default function ProductInfo({
    product,
    variantStyle = 'full',
    hideActionIcons = false,
    headerAction,
    hideDescription = false,
    modelCodePromise,
    modelName,
    showBrandLogo = false,
    // @sfdc-extension-line SFDC_EXT_RATINGS_REVIEWS
    hideRating = false,
    // @sfdc-extension-line SFDC_EXT_RATINGS_REVIEWS
    disableRatingInteraction = false,
}: ProductInfoProps): ReactElement {
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    const { t } = useTranslation('product');

    const isCompactStyle = variantStyle === 'compact';
    const isPdpStyle = variantStyle === 'pdp';

    // `null` for a brand outside the eight family marks (or none at all) — the text treatment is
    // the fallback, not an error state, since the catalogue's brand field is free text.
    const brandKey = showBrandLogo ? resolveBrandKey(product.brand) : null;
    // Trimmed, because a whitespace-only brand is truthy: it used to render an empty bold row
    // plus its 12px gap, which reads as a layout bug rather than as "no brand".
    const brandText = product.brand?.trim();

    return (
        <div className="relative grid gap-4">
            {/* Action icons — top-right */}
            {!isCompactStyle && !hideActionIcons && (
                <div className="absolute top-0 right-0 flex items-center gap-2 z-10">
                    <WishlistButton
                        product={{
                            productId: product.id,
                            productName: product.name,
                            price: product.price,
                            image: product.imageGroups?.[0]?.images?.[0],
                        }}
                        surface="pdp"
                        size="sm"
                        className="!static border border-border bg-background/90 hover:border-muted-foreground/50 hover:bg-background"
                    />
                    <ShareButton
                        product={product}
                        size="sm"
                        className="!static border border-border bg-background/90 hover:bg-background hover:border-muted-foreground/50 [&_svg]:stroke-[2]"
                    />
                </div>
            )}

            {/* Compact style: brand (uppercase) then product name */}
            {isCompactStyle && (
                <>
                    {brandText && (
                        <p className="text-xs font-normal leading-none uppercase tracking-wide text-secondary-foreground">
                            {brandText}
                        </p>
                    )}
                    <h2 className="text-3xl font-bold text-card-foreground tracking-tight">{product.name}</h2>
                </>
            )}

            {/* Product Title, Identifier, Description */}
            {!isCompactStyle && (
                <div className="flex items-start justify-between gap-4">
                    <div className={`${hideActionIcons ? '' : 'pr-20'} min-w-0`}>
                        {isPdpStyle ? (
                            <PdpHeaderText
                                product={product}
                                brandKey={brandKey}
                                brandText={brandText}
                                modelName={modelName}
                                modelCodePromise={modelCodePromise}
                            />
                        ) : (
                            <FullHeaderText
                                product={product}
                                brandKey={brandKey}
                                brandText={brandText}
                                skuLabel={t('sku')}
                            />
                        )}
                        {/* Outside both header groups: Figma's PDP header has no description at all, so
                            this 8px is ours and must not inherit the design's 12px rhythm. */}
                        {!hideDescription && product.shortDescription && (
                            <p className="mt-2 text-base font-normal leading-6 text-accent-foreground">
                                {product.shortDescription}
                            </p>
                        )}
                    </div>
                    {headerAction ? <div className="pt-1 shrink-0">{headerAction}</div> : null}
                </div>
            )}
            {/* Rating summary - visible on both mobile and desktop */}
            {/* @sfdc-extension-block-start SFDC_EXT_RATINGS_REVIEWS */}
            {!isCompactStyle && !hideRating && (
                <UITarget targetId="sfcc.pdp.reviews.rating">
                    <ProductRatingSummary interactive={!disableRatingInteraction} />
                </UITarget>
            )}
            {/* @sfdc-extension-block-end SFDC_EXT_RATINGS_REVIEWS */}

            {/* Product Bundle/Set Notice */}
            {(isProductASet || isProductABundle) && (
                <div className="rounded-ui bg-primary/10 border border-primary p-4">
                    <p className="text-sm text-primary">
                        {isProductASet ? t('productSetNotice') : t('productBundleNotice')}
                    </p>
                </div>
            )}
        </div>
    );
}
